import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Newspaper, Sparkles, Loader2, Plus, RefreshCw, Filter, Volume2,
  Radio, FileText, MessageSquare, Mic, Check, X, ChevronRight, ChevronDown,
  Copy, Trash2, AlertTriangle, Globe, Rocket, Image as ImageIcon, Upload, ZoomIn
} from "lucide-react";
import PageHeader from "@/components/PageHeader";
import ImageSwapLightbox from "@/components/ImageSwapLightbox";
import NewsVideoStudio from "@/components/NewsVideoStudio";
import NewsAnalytics from "@/components/NewsAnalytics";
import PromptSuggestionButton from "@/components/PromptSuggestionButton";
import AiWriteScriptModal, { AiWriteButton } from "@/components/AiWriteScriptModal";
import VisualStyleSelector from "@/components/news/VisualStyleSelector";
import ContentLanguageSelect from "@/components/ContentLanguageSelect";
import TranslateBroadcastModal from "@/components/TranslateBroadcastModal";
import ImageCropModal from "@/components/ImageCropModal";
import { PlatformIcon, getPlatformMeta } from "@/components/PlatformIcon";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";

const STATUS_PILL = {
  draft:     { label: "DRAFT",     cls: "border-line bg-bg-elevated text-ink-muted" },
  approved:  { label: "APPROVED",  cls: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" },
  published: { label: "PUBLISHED", cls: "border-brand-amber/50 bg-brand-amber/10 text-brand-amber" },
  archived:  { label: "ARCHIVED",  cls: "border-rose-500/40 bg-rose-500/10 text-rose-300" },
};

export default function NewsCenter() {
  const [stories, setStories] = useState([]);
  const [meta, setMeta] = useState({ sources: [], genres: [], categories: [], voices: [] });
  const [filterGenre, setFilterGenre] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [ingesting, setIngesting] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [showManual, setShowManual] = useState(false);
  const [view, setView] = useState("stories"); // stories | analytics

  // Batch broadcast selection + job tracking
  const [selected, setSelected] = useState(() => new Set());
  const [batchJob, setBatchJob] = useState(null);
  const [batchBusy, setBatchBusy] = useState(false);
  const toggleSel = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const clearSel = () => setSelected(new Set());

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterGenre) params.append("genre", filterGenre);
      if (filterStatus) params.append("status", filterStatus);
      const [a, b] = await Promise.all([
        api.get(`/news/stories?${params}`),
        api.get("/news/sources"),
      ]);
      setStories(a.data || []);
      setMeta(b.data || { sources: [], genres: [], voices: [] });
    } catch { toast.error("Failed to load news"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filterGenre, filterCategory, filterStatus]);

  const ingest = async () => {
    setIngesting(true);
    try {
      const { data } = await api.post("/news/ingest");
      toast.success(`Pulled ${data.inserted} new stories · skipped ${data.skipped} duplicates`);
      if (data.errors?.length) {
        toast.warning(`${data.errors.length} feed(s) had issues — see logs`);
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ingest failed");
    } finally { setIngesting(false); }
  };

  const active = useMemo(() => stories.find((s) => s.story_id === activeId), [stories, activeId]);

  // Batch broadcast launch + poll
  const launchBatch = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setBatchBusy(true);
    try {
      const { data } = await api.post("/news/stories/broadcast-batch", { story_ids: ids });
      setBatchJob({ job_id: data.job_id, total: data.total, completed: 0, succeeded: 0, failed: 0, status: "running", results: [] });
      toast.success(`Batch broadcast started for ${data.total} stor${data.total === 1 ? "y" : "ies"}`);
      clearSel();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Batch broadcast failed to start");
    } finally { setBatchBusy(false); }
  };

  useEffect(() => {
    if (!batchJob || batchJob.status === "done") return;
    const id = batchJob.job_id;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/news/broadcast-jobs/${id}`);
        setBatchJob(data);
        if (data.status === "done") {
          clearInterval(t);
          toast.success(`Batch complete · ${data.succeeded} ok · ${data.failed} failed`);
          load(); // refresh story statuses
        }
      } catch { /* keep polling */ }
    }, 4000);
    return () => clearInterval(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchJob?.job_id]);

  if (active) {
    return (
      <StoryDetail
        story={active}
        voices={meta.voices}
        genres={meta.genres}
        onBack={() => { setActiveId(null); load(); }}
        onChange={(d) => setStories((arr) => arr.map((s) => s.story_id === d.story_id ? d : s))}
        onListRefresh={load}
      />
    );
  }

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto animate-fade-up" data-testid="news-center-page">
      <PageHeader
        eyebrow="// AI MUSIC NEWS CENTER"
        title="Fresh music news, every hour."
        description="Pull stories from Billboard, Rolling Stone, MBW, Grammy & more — AI rewrites each into radio scripts, a blog, six social posts, a podcast segment, and an audio bulletin."
        action={
          <div className="flex items-center gap-2">
            <button onClick={() => setShowManual(true)} data-testid="news-add-manual" className="inline-flex items-center gap-2 rounded-md border border-line bg-bg-elevated px-4 py-2 text-sm text-ink-secondary hover:text-ink-primary transition">
              <Plus className="h-4 w-4" /> Add story
            </button>
            <button onClick={ingest} disabled={ingesting} data-testid="news-ingest-btn" className="inline-flex items-center gap-2 rounded-md bg-brand-amber px-5 py-2.5 type-button-label text-black amber-glow disabled:opacity-50">
              {ingesting ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Pull latest
            </button>
          </div>
        }
      />

      {/* Top-level view tabs */}
      <div className="mt-4 inline-flex rounded-lg border border-line bg-bg-elevated p-1 gap-1" data-testid="news-view-switch">
        {[
          { id: "stories",   label: "Stories" },
          { id: "analytics", label: "Analytics" },
        ].map((v) => (
          <button
            key={v.id}
            onClick={() => setView(v.id)}
            data-testid={`news-view-${v.id}`}
            className={`px-4 py-1.5 text-xs font-mono uppercase tracking-wider rounded-md transition ${view === v.id ? "bg-brand-amber text-black font-semibold" : "text-ink-secondary hover:text-ink-primary"}`}
          >{v.label}</button>
        ))}
      </div>

      {view === "analytics" && (
        <div className="mt-6">
          <NewsAnalytics />
        </div>
      )}

      {view === "stories" && (
      <>
      {showManual && (
        <ManualStoryForm
          genres={meta.genres}
          onCancel={() => setShowManual(false)}
          onSaved={(d) => { setShowManual(false); setStories((arr) => [d, ...arr]); toast.success("Story saved"); }}
        />
      )}

      <div className="my-6 flex flex-wrap items-center gap-2" data-testid="news-filters">
        <Filter className="h-3.5 w-3.5 text-ink-muted" />
        <Select value={filterCategory || "all"} onValueChange={(v) => setFilterCategory(v === "all" ? "" : v)}>
          <SelectTrigger className="h-8 w-44 bg-bg border-line text-xs" data-testid="news-filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {(meta.categories || []).map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterGenre || "all"} onValueChange={(v) => setFilterGenre(v === "all" ? "" : v)}>
          <SelectTrigger className="h-8 w-40 bg-bg border-line text-xs" data-testid="news-filter-genre"><SelectValue placeholder="Genre" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All genres</SelectItem>
            {meta.genres.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="h-8 w-40 bg-bg border-line text-xs" data-testid="news-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="published">Published</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto text-[10px] font-mono uppercase tracking-wider text-ink-muted">
          {stories.length} {stories.length === 1 ? "story" : "stories"}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-ink-muted text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : stories.length === 0 ? (
        <div className="rounded-xl border border-dashed border-line bg-bg-elevated/40 p-10 text-center" data-testid="news-empty">
          <Newspaper className="h-8 w-8 text-brand-amber mx-auto mb-3" />
          <h3 className="type-section-heading text-ink-primary mb-1">No stories yet</h3>
          <div className="type-body text-ink-muted mb-4">Hit <span className="text-brand-amber">Pull latest</span> to bring in fresh stories from Billboard, Rolling Stone, MBW, Grammy and more.</div>
          <button onClick={ingest} disabled={ingesting} data-testid="news-empty-pull" className="inline-flex items-center gap-2 rounded-md bg-brand-amber px-4 py-2 type-button-label text-black amber-glow disabled:opacity-50">
            {ingesting ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Pull latest news
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="news-grid">
          {stories.map((s) => {
            const badge = STATUS_PILL[s.status] || STATUS_PILL.draft;
            const hasGen = Object.keys(s.generated || {}).length > 0;
            const isSelected = selected.has(s.story_id);
            return (
              <div
                key={s.story_id}
                data-testid={`news-card-${s.story_id}`}
                className={`relative rounded-xl border bg-bg-surface p-5 hover:border-brand-amber/40 transition group ${isSelected ? "border-violet-500/60 bg-violet-500/[0.04]" : "border-line"}`}
              >
                {/* Multi-select checkbox */}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); toggleSel(s.story_id); }}
                  data-testid={`news-select-${s.story_id}`}
                  aria-label={isSelected ? "Deselect story" : "Select story for batch broadcast"}
                  className={`absolute top-3 left-3 h-5 w-5 rounded border flex items-center justify-center transition ${isSelected ? "bg-violet-500 border-violet-400 text-white" : "border-line bg-bg-elevated hover:border-violet-400/60"}`}
                >
                  {isSelected && <Check className="h-3 w-3" />}
                </button>

                <button
                  type="button"
                  onClick={() => setActiveId(s.story_id)}
                  className="text-left w-full pl-7"
                  data-testid={`news-open-${s.story_id}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <span className={`shrink-0 rounded-md border px-2 py-0.5 font-mono text-[10px] tracking-wider ${badge.cls}`}>{badge.label}</span>
                    <span className="text-[10px] font-mono text-ink-muted">{s.source}</span>
                  </div>
                  {s.category && (
                    <span className="inline-block mb-2 rounded-full bg-bg-elevated border border-line px-2 py-0.5 text-[9px] font-mono uppercase tracking-wider text-ink-muted">{s.category}</span>
                  )}
                  <div className="type-list-title text-ink-primary line-clamp-2 mb-2">{s.headline}</div>
                  {s.summary && <p className="type-body text-ink-secondary line-clamp-3 mb-3">{s.summary}</p>}
                  <div className="flex items-center flex-wrap gap-1.5 mb-3">
                    {(s.genres || []).slice(0, 3).map((g) => (
                      <span key={g} className="rounded-full bg-bg-elevated border border-line px-2 py-0.5 text-[10px] text-ink-muted">{g}</span>
                    ))}
                    {typeof s.trust_score === "number" && (
                      <span className={`ml-auto rounded-full border px-2 py-0.5 text-[10px] font-mono ${
                        s.trust_score >= 75 ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                        : s.trust_score >= 50 ? "border-cyan-500/40 bg-cyan-500/10 text-cyan-200"
                        : "border-rose-500/40 bg-rose-500/10 text-rose-300"
                      }`} data-testid={`news-trust-${s.story_id}`}>
                        ⓘ {s.trust_score}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-between text-[11px] text-ink-muted">
                    <span>{hasGen ? "✓ Content ready" : "Not generated"}</span>
                    <ChevronRight className="h-3 w-3 group-hover:translate-x-0.5 transition" />
                  </div>
                </button>
              </div>
            );
          })}
        </div>
      )}
      </>
      )}

      {/* Floating batch action bar */}
      {selected.size > 0 && !batchJob && (
        <div
          data-testid="batch-action-bar"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-30 flex items-center gap-3 rounded-full border px-2 py-2 pl-5 shadow-2xl animate-fade-up"
          style={{
            background: "rgba(15, 23, 42, 0.95)",
            borderColor: "rgba(139, 92, 246, 0.45)",
            backdropFilter: "blur(12px)",
          }}
        >
          <span className="text-xs text-ink-secondary">
            <span className="font-display text-base font-bold text-brand-amber">{selected.size}</span> selected
          </span>
          <button
            onClick={clearSel}
            data-testid="batch-clear"
            className="text-[11px] text-ink-muted hover:text-ink-primary"
          >Clear</button>
          <button
            onClick={launchBatch}
            disabled={batchBusy}
            data-testid="batch-broadcast-btn"
            className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-bold text-white disabled:opacity-50 transition hover:scale-[1.03]"
            style={{
              background: "linear-gradient(135deg, #8B5CF6 0%, #A855F7 50%, #FF8A1F 100%)",
              boxShadow: "0 8px 24px -8px rgba(139,92,246,0.6)",
            }}
          >
            {batchBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}
            Broadcast {selected.size} {selected.size === 1 ? "story" : "stories"}
          </button>
        </div>
      )}

      {/* Batch progress / result panel */}
      {batchJob && (
        <BatchProgressPanel job={batchJob} onClose={() => setBatchJob(null)} />
      )}
    </div>
  );
}

function ManualStoryForm({ genres, onCancel, onSaved }) {
  const [f, setF] = useState({ headline: "", summary: "", link: "", genres: [] });
  const [busy, setBusy] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const toggle = (g) => setF((x) => ({ ...x, genres: x.genres.includes(g) ? x.genres.filter((y) => y !== g) : [...x.genres, g] }));
  const save = async () => {
    if (!f.headline.trim()) { toast.error("Headline required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/news/stories", f);
      onSaved(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-6 rounded-xl border border-line bg-bg-surface p-6 space-y-4" data-testid="news-manual-form">
      <div className="flex items-center justify-between">
        <div className="text-sm text-ink-muted font-mono uppercase tracking-wider">Manual story</div>
        <AiWriteButton onClick={() => setAiOpen(true)} testid="news-manual-ai-write" label="Write for me" />
      </div>
      <div>
        <div className="flex items-center justify-between gap-2">
          <Label className="text-xs uppercase tracking-wider text-ink-secondary">Headline *</Label>
          <PromptSuggestionButton
            surface="news"
            fieldName="headline"
            context={{ summary: f.summary }}
            onSelect={(val) => setF((s) => ({ ...s, headline: val }))}
            testid="news-suggest-headline"
          />
        </div>
        <Input value={f.headline} onChange={(e) => setF({ ...f, headline: e.target.value })} className="mt-1 bg-bg border-line" data-testid="news-manual-headline" />
      </div>
      <div>
        <div className="flex items-center justify-between gap-2">
          <Label className="text-xs uppercase tracking-wider text-ink-secondary">Summary</Label>
          <PromptSuggestionButton
            surface="news"
            fieldName="summary"
            context={{ headline: f.headline }}
            onSelect={(val) => setF((s) => ({ ...s, summary: val }))}
            testid="news-suggest-summary"
          />
        </div>
        <Textarea rows={3} value={f.summary} onChange={(e) => setF({ ...f, summary: e.target.value })} className="mt-1 bg-bg border-line" data-testid="news-manual-summary" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider text-ink-secondary">Source link (optional)</Label>
        <Input value={f.link} onChange={(e) => setF({ ...f, link: e.target.value })} className="mt-1 bg-bg border-line font-mono text-xs" data-testid="news-manual-link" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider text-ink-secondary mb-2 block">Genres</Label>
        <div className="flex flex-wrap gap-1.5">
          {genres.map((g) => {
            const on = f.genres.includes(g);
            return (
              <button key={g} onClick={() => toggle(g)} data-testid={`news-genre-${g}`}
                className={`rounded-full border px-3 py-1 text-[11px] transition ${on ? "border-brand-amber bg-brand-amber/15 text-brand-amber" : "border-line bg-bg-elevated text-ink-muted hover:text-ink-primary"}`}>
                {g}
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex items-center justify-end gap-2">
        <button onClick={onCancel} data-testid="news-manual-cancel" className="rounded-md border border-line bg-bg-elevated px-4 py-2 type-button-label text-ink-secondary hover:text-ink-primary">Cancel</button>
        <button onClick={save} disabled={busy} data-testid="news-manual-save" className="inline-flex items-center gap-2 rounded-md bg-brand-amber px-5 py-2 type-button-label text-black amber-glow disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Save story
        </button>
      </div>

      <AiWriteScriptModal
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        defaultScriptType="news"
        defaultTone="authoritative"
        defaultLengthSec={30}
        initialTopic={f.headline}
        onApply={({ title, script }) => {
          setF((s) => ({
            ...s,
            headline: s.headline?.trim() ? s.headline : (title || s.headline),
            summary:  script,
          }));
          setAiOpen(false);
          toast.success("Story filled in. Add a source link if you have one.");
        }}
      />
    </div>
  );
}

function StoryDetail({ story, voices, genres, onBack, onChange, onListRefresh }) {
  const [s, setS] = useState(story);
  const [gen, setGen] = useState({ run: false, audio: false });
  const [voice, setVoice] = useState("female_news");
  const [audioDur, setAudioDur] = useState("radio_60");
  const [genreFocus, setGenreFocus] = useState((story.genres && story.genres[0]) || "");
  // Update #133 · Language Center — per-story override for AI writer output.
  // Auto-hydrates from ContentLanguageSelect (which reads the user's stored
  // content_language preference on mount).
  const [contentLang, setContentLang] = useState(story.language || "en");

  useEffect(() => { setS(story); }, [story]);
  const g = s.generated || {};
  const audioSrc = s.audio_asset_id ? `${API_BASE}/imaging/assets/${s.audio_asset_id}/audio` : null;
  const badge = STATUS_PILL[s.status] || STATUS_PILL.draft;

  const generate = async () => {
    setGen((x) => ({ ...x, run: true }));
    try {
      const { data } = await api.post(`/news/stories/${s.story_id}/generate`, {
        genre_focus: genreFocus,
        language: contentLang || undefined,
      });
      setS(data); onChange(data); toast.success("Content package generated");
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || "Generation failed";
      if (status === 402) {
        toast.error("AI budget exhausted — top up at Emergent → Profile → Universal Key → Add Balance.", { duration: 8000 });
      } else if (status === 429) {
        toast.error("AI provider rate-limited — try again in a few seconds.");
      } else if (status === 504) {
        toast.error("AI provider timed out — please retry.");
      } else {
        toast.error(detail);
      }
    }
    finally { setGen((x) => ({ ...x, run: false })); }
  };

  const generateAudio = async () => {
    if (!g[audioDur]) { toast.error("Generate the script first"); return; }
    setGen((x) => ({ ...x, audio: true }));
    try {
      const { data } = await api.post(`/news/stories/${s.story_id}/audio`, { voice, duration: audioDur });
      const refreshed = await api.get(`/news/stories/${s.story_id}`);
      setS(refreshed.data); onChange(refreshed.data);
      toast.success(`Audio bulletin ready (${data.duration_ms / 1000}s)`);
    } catch (e) { toast.error(e.response?.data?.detail || "Audio failed"); }
    finally { setGen((x) => ({ ...x, audio: false })); }
  };

  const patch = async (updates) => {
    try {
      const { data } = await api.patch(`/news/stories/${s.story_id}`, updates);
      setS(data); onChange(data);
    } catch { toast.error("Update failed"); }
  };

  const [bcastBusy, setBcastBusy] = useState(false);
  const [bcastResult, setBcastResult] = useState(null);
  const [wpBusy, setWpBusy] = useState(false);
  const [imgBusy, setImgBusy] = useState("");
  const [cropFile, setCropFile] = useState(null);
  const [showImg, setShowImg] = useState(false);
  const imgFileRef = useRef(null);
  const genFeaturedImage = async (strategy) => {
    setImgBusy(strategy);
    try {
      const { data } = await api.post(`/news/stories/${s.story_id}/featured-image`, { strategy });
      setS(data); onChange(data);
      toast.success(strategy === "unsplash_photo" ? "Unsplash photo attached — credit added" : "Branded title card generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Featured image failed");
    } finally { setImgBusy(""); }
  };
  const uploadFeaturedImage = async (blob) => {
    setImgBusy("upload");
    try {
      const form = new FormData();
      form.append("file", blob, "featured.jpg");
      const { data } = await api.post(`/news/stories/${s.story_id}/featured-image/upload`, form,
        { headers: { "Content-Type": "multipart/form-data" } });
      setS(data); onChange(data);
      toast.success("Photo uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setImgBusy("");
      if (imgFileRef.current) imgFileRef.current.value = "";
    }
  };
  // Update #178 — one-tap Translate + Broadcast modal
  const [translateOpen, setTranslateOpen] = useState(false);
  // Visual Style Selector — Update #81
  // Reads the default preset chosen in NewsStudio (persisted to localStorage)
  // so users can pre-pick a style there and have it auto-applied here.
  const [visualPick, setVisualPick] = useState(() => {
    try {
      const raw = localStorage.getItem("bf_news_visual_style_default");
      const parsed = raw ? JSON.parse(raw) : null;
      if (parsed && parsed.visual_style) return parsed;
    } catch { /* corrupt cache, ignore */ }
    return { visual_style: "avatar", avatar_id: "amy", avatar_source_url: undefined };
  });
  useEffect(() => {
    try { localStorage.setItem("bf_news_visual_style_default", JSON.stringify(visualPick)); }
    catch { /* storage blocked */ }
  }, [visualPick]);

  const publishToWP = async () => {
    if (Object.keys(g).length === 0) { toast.error("Generate the package first (Step 1)"); return; }
    setWpBusy(true);
    try {
      const { data } = await api.post("/blog-connector/wordpress/publish", {
        story_id: s.story_id,
        status: "draft",  // safety default — user reviews in WP-Admin
        featured_image_url: s.image_url || null,
      });
      toast.success(`WordPress draft created · #${data.post.id}`, {
        duration: 8000,
        action: data.post.link ? { label: "View", onClick: () => window.open(data.post.link, "_blank") } : undefined,
      });
      // refresh to show wp_publish badge
      const refreshed = await api.get(`/news/stories/${s.story_id}`);
      setS(refreshed.data); onChange(refreshed.data);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || "WordPress publish failed";
      if (status === 404 && /not connected/i.test(detail)) {
        toast.error("Connect WordPress first — head to Blog Connector.", {
          duration: 8000,
          action: { label: "Open", onClick: () => window.location.assign("/dashboard/blog-connector") },
        });
      } else {
        toast.error(detail, { duration: 8000 });
      }
    } finally { setWpBusy(false); }
  };
  const broadcast = async () => {
    if (Object.keys(g).length === 0) { toast.error("Generate the package first (Step 1)"); return; }
    setBcastBusy(true);
    setBcastResult(null);
    const styleLabel = visualPick.visual_style === "avatar" ? "AI presenter" :
                       visualPick.visual_style === "image"  ? "Topic image" : "Waveform";
    toast.message(`Broadcasting · ${styleLabel} render — may take ${visualPick.visual_style === "avatar" ? "~30s" : "~15s"}`);
    try {
      const { data } = await api.post(`/news/stories/${s.story_id}/broadcast`, {
        voice, video_fmt: "vertical", video_theme: "amber_dark",
        visual_style: visualPick.visual_style,
        avatar_id: visualPick.avatar_id,
        avatar_source_url: visualPick.avatar_source_url,
      });
      setBcastResult(data);
      const v = data.stages.video;
      const tp = data.stages.text_posts;
      const vp = data.stages.video_posts;
      // Surface graceful fallback from avatar → waveform
      const fellBack = v.ok && v.visual_meta?.fallback_reason && v.visual_style !== visualPick.visual_style;
      const note = fellBack
        ? ` · avatar failed (${v.visual_meta.fallback_reason.slice(0, 60)}) — used waveform`
        : (v.ok && v.visual_style === "image" && v.visual_meta?.source === "fallback"
            ? " · image not found, used dark fallback" : "");
      toast.success(`Broadcast complete: ${tp.count} text + ${vp.count} video posts queued${v.ok ? " · MP4 rendered" : ""}${note}`);
      // refresh
      const refreshed = await api.get(`/news/stories/${s.story_id}`);
      setS(refreshed.data); onChange(refreshed.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Broadcast failed");
    } finally { setBcastBusy(false); }
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto animate-fade-up space-y-6" data-testid="news-detail">
      <button onClick={onBack} data-testid="news-back" className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted hover:text-cyan-300 transition">← All stories</button>

      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-amber/80 mb-1">// {s.source}</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">{s.headline}</h1>
          {s.summary && <p className="mt-2 max-w-3xl text-sm text-ink-secondary">{s.summary}</p>}
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <span className={`rounded-md border px-2 py-0.5 font-mono text-[10px] tracking-wider ${badge.cls}`} data-testid="news-status">{badge.label}</span>
            {(s.genres || []).map((x) => <span key={x} className="rounded-full bg-bg-elevated border border-line px-2 py-0.5 text-[10px] text-ink-muted">{x}</span>)}
            {s.link && <a href={s.link} target="_blank" rel="noreferrer" className="text-[11px] text-brand-amber hover:underline flex items-center gap-1"><Globe className="h-3 w-3" /> Source</a>}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <div className="flex items-center gap-2 text-xs text-ink-muted">
            <span>Auto-publish</span>
            <Switch checked={!!s.auto_publish} onCheckedChange={(v) => patch({ auto_publish: v })} data-testid="news-auto-publish" />
          </div>
          <button
            onClick={publishToWP}
            disabled={wpBusy || Object.keys(g).length === 0}
            data-testid="news-wp-publish-btn"
            title="Publish this story as a draft to your connected WordPress site"
            className="inline-flex items-center gap-2 rounded-md border border-sky-500/40 bg-sky-500/10 px-3 py-2.5 text-xs font-bold text-sky-300 hover:bg-sky-500/20 disabled:opacity-50 transition"
          >
            {wpBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Globe className="h-3.5 w-3.5" />}
            {wpBusy ? "Sending…" : "WordPress"}
            {s.wp_publish?.post_id && <span className="text-[9px] font-mono ml-1 px-1 py-0.5 rounded bg-emerald-500/20 text-emerald-300">✓</span>}
          </button>
          <button
            onClick={() => setTranslateOpen(true)}
            disabled={Object.keys(g).length === 0}
            data-testid="news-translate-broadcast-btn"
            title="Clone this story into other languages and broadcast every variant in one tap"
            className="inline-flex items-center gap-2 rounded-md border border-cyan-500/40 bg-cyan-500/10 px-3 py-2.5 text-xs font-bold text-cyan-300 hover:bg-cyan-500/20 disabled:opacity-50 transition"
          >
            <Globe className="h-3.5 w-3.5" />
            Translate + Broadcast
          </button>
          <button
            onClick={broadcast}
            disabled={bcastBusy || Object.keys(g).length === 0}
            data-testid="news-broadcast-btn"
            title="Approve · mix audio · render video · post text + MP4 to socials — all in one tap"
            className="inline-flex items-center gap-2 rounded-md px-4 py-2.5 text-sm font-bold text-white disabled:opacity-50 transition hover:scale-[1.02]"
            style={{
              background: "linear-gradient(135deg, #8B5CF6 0%, #A855F7 50%, #FF8A1F 100%)",
              boxShadow: "0 8px 24px -8px rgba(139,92,246,0.6)",
            }}
          >
            {bcastBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}
            {bcastBusy ? "Broadcasting…" : "Approve & broadcast"}
          </button>
        </div>
      </div>

      <TranslateBroadcastModal
        open={translateOpen}
        onClose={() => setTranslateOpen(false)}
        story={s}
        parentLang={s.language || contentLang || "en"}
        broadcastOptions={{
          voice: voice,
          visual_style: visualPick.visual_style,
          avatar_id: visualPick.avatar_id,
          avatar_source_url: visualPick.avatar_source_url,
        }}
        onDone={() => { onListRefresh?.(); }}
      />

      {/* Visual Style Selector — picks middle layer of the rendered video */}
      <VisualStyleSelector
        value={visualPick}
        onChange={setVisualPick}
        data-testid="news-center-visual-style"
      />

      {/* Featured image — branded title card or Unsplash photo */}
      <section className="rounded-xl border border-line bg-bg-surface p-5" data-testid="news-featured-image">
        <div className="flex flex-wrap items-center gap-4">
          {s.image_url ? (
            <button type="button" onClick={() => setShowImg(true)} title="Click to enlarge"
              className="group relative shrink-0 cursor-zoom-in rounded-lg focus:outline-none"
              data-testid="news-featured-image-zoom">
              <img src={s.image_url} alt="featured" data-testid="news-featured-image-thumb"
                className="h-20 w-36 rounded-lg object-cover border border-line group-hover:border-brand-amber/60 transition" />
              <span className="absolute inset-0 rounded-lg bg-black/0 group-hover:bg-black/40 transition grid place-items-center">
                <ZoomIn className="h-4 w-4 text-white opacity-0 group-hover:opacity-100 transition" />
              </span>
            </button>
          ) : (
            <div className="h-20 w-36 rounded-lg border border-dashed border-line grid place-items-center text-[10px] text-ink-muted shrink-0">
              No featured image
            </div>
          )}
          <div className="flex-1 min-w-[220px]">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">// Featured image</div>
            <p className="text-[11px] text-ink-secondary">Used as the WordPress featured image, Instagram photo and station-blog thumbnail.</p>
            {s.image_credit?.photographer && (
              <div className="mt-1 text-xs text-ink-secondary" data-testid="news-featured-image-credit">
                Photo by{" "}
                <a href={s.image_credit.photographer_url} target="_blank" rel="noreferrer" className="text-sky-300 hover:underline">{s.image_credit.photographer}</a>
                {" "}on{" "}
                <a href={s.image_credit.unsplash_url || "https://unsplash.com/?utm_source=broadcastflow_ai&utm_medium=referral"} target="_blank" rel="noreferrer" className="text-sky-300 hover:underline">Unsplash</a>
              </div>
            )}
          </div>
          <div className="flex gap-2 shrink-0">
            <button onClick={() => genFeaturedImage("branded_card")} disabled={!!imgBusy}
              data-testid="news-featured-card-btn"
              className="inline-flex items-center gap-1.5 rounded-md border border-brand-amber/40 bg-brand-amber/10 px-3 py-2 text-xs font-semibold text-brand-amber hover:bg-brand-amber/20 disabled:opacity-50 transition">
              {imgBusy === "branded_card" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageIcon className="h-3.5 w-3.5" />}
              Branded card
            </button>
            <button onClick={() => genFeaturedImage("unsplash_photo")} disabled={!!imgBusy}
              data-testid="news-featured-unsplash-btn"
              className="inline-flex items-center gap-1.5 rounded-md border border-sky-500/40 bg-sky-500/10 px-3 py-2 text-xs font-semibold text-sky-300 hover:bg-sky-500/20 disabled:opacity-50 transition">
              {imgBusy === "unsplash_photo" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageIcon className="h-3.5 w-3.5" />}
              Unsplash photo
            </button>
            <input ref={imgFileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) setCropFile(f); }} data-testid="news-featured-upload-input" />
            <button onClick={() => imgFileRef.current?.click()} disabled={!!imgBusy}
              data-testid="news-featured-upload-btn"
              title="Upload your own photo (PNG/JPG/WEBP, 10 MB max)"
              className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs font-semibold text-emerald-300 hover:bg-emerald-500/20 disabled:opacity-50 transition">
              {imgBusy === "upload" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
              Upload
            </button>
            {cropFile && (
              <ImageCropModal file={cropFile} aspect={1200 / 630} maxWidth={1600} title="Frame featured image"
                onCancel={() => { setCropFile(null); if (imgFileRef.current) imgFileRef.current.value = ""; }}
                onCropped={(blob) => { setCropFile(null); uploadFeaturedImage(blob); }} />
            )}
          </div>
        </div>
      </section>

      {showImg && s.image_url && (
        <ImageSwapLightbox
          imageUrl={s.image_url}
          title={s.headline || s.title || "Featured image"}
          creditText={s.image_credit?.photographer ? `Photo by ${s.image_credit.photographer} on Unsplash` : ""}
          credit={s.image_credit?.photographer ? s.image_credit : null}
          altsUrl={`/news/stories/${s.story_id}/featured-image/alternatives`}
          selectUrl={`/news/stories/${s.story_id}/featured-image/select`}
          unsplashOptionsUrl={`/news/stories/${s.story_id}/featured-image/unsplash-options`}
          unsplashSelectUrl={`/news/stories/${s.story_id}/featured-image/unsplash-select`}
          onClose={() => setShowImg(false)}
          onSelected={(data) => { setS(data); onChange(data); }}
          altAspectClass="aspect-[1200/630]"
          testIdBase="news-featured"
        />
      )}

      {bcastResult && (
        <section
          data-testid="news-broadcast-result"
          className="rounded-xl border p-5 animate-fade-up"
          style={{
            background: "linear-gradient(135deg, rgba(139,92,246,0.08), rgba(245,158,11,0.04))",
            borderColor: "rgba(245,158,11,0.30)",
          }}
        >
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="font-mono type-status-badge uppercase tracking-[0.22em] text-brand-amber/80 flex items-center gap-1.5">
                <Rocket className="h-3 w-3" /> // BROADCAST COMPLETE
              </div>
              <h3 className="type-section-heading text-ink-primary mt-0.5">5-stage cascade summary</h3>
            </div>
            <button
              onClick={() => setBcastResult(null)}
              className="text-ink-muted hover:text-ink-primary"
              data-testid="news-broadcast-result-close"
            >✕</button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
            <StageTile label="Approved"   ok={bcastResult.stages.approve} />
            <StageTile label="Anchor MP3" ok={bcastResult.stages.anchor_mix.ok}
                       skipped={bcastResult.stages.anchor_mix.skipped}
                       error={bcastResult.stages.anchor_mix.error}
                       extra={
                         bcastResult.stages.anchor_mix.ok && bcastResult.stages.anchor_mix.bed_source === "now_playing"
                           ? `Bed: matched live (${bcastResult.stages.anchor_mix.bed_source_meta?.mood || ""})`
                         : bcastResult.stages.anchor_mix.ok && bcastResult.stages.anchor_mix.bed_source === "station_genre"
                           ? `Bed: ${bcastResult.stages.anchor_mix.bed_subtype} (station genre)`
                         : bcastResult.stages.anchor_mix.ok
                           ? `Bed: ${bcastResult.stages.anchor_mix.bed_subtype || ""}`
                         : null
                       } />
            <StageTile label="Video MP4"  ok={bcastResult.stages.video.ok}
                       skipped={bcastResult.stages.video.skipped}
                       error={bcastResult.stages.video.error}
                       extra={bcastResult.stages.video.ok ? `${bcastResult.stages.video.fmt} · ${bcastResult.stages.video.theme}` : null} />
            <StageTile label="Text posts" ok={bcastResult.stages.text_posts.count > 0}
                       extra={`${bcastResult.stages.text_posts.count} queued${bcastResult.stages.text_posts.skipped.length ? ` · ${bcastResult.stages.text_posts.skipped.length} skipped` : ""}`} />
            <StageTile label="Video posts" ok={bcastResult.stages.video_posts.count > 0}
                       extra={`${bcastResult.stages.video_posts.count} queued${bcastResult.stages.video_posts.skipped.length ? ` · ${bcastResult.stages.video_posts.skipped.length} skipped` : ""}`} />
          </div>
          {(bcastResult.stages.text_posts.platforms.length + bcastResult.stages.video_posts.platforms.length) > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-1.5 text-[11px] text-ink-muted">
              <span className="font-mono uppercase tracking-wider">Dispatched to:</span>
              {[...new Set([
                ...bcastResult.stages.text_posts.platforms,
                ...bcastResult.stages.video_posts.platforms,
              ])].map((p) => (
                <span key={p} className="rounded-md border border-line bg-bg-elevated/60 px-2 py-0.5 text-ink-primary capitalize">{p}</span>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Generate panel */}
      <section className="rounded-xl border border-line bg-bg-surface p-6" data-testid="news-generate-panel">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <div>
            <div className="font-mono type-status-badge uppercase tracking-[0.2em] text-brand-amber/80">// STEP 1</div>
            <h3 className="type-section-heading text-ink-primary">Generate the package</h3>
            <p className="type-body text-ink-muted">Radio scripts (30/60/120s), blog article, 6 social posts, podcast segment — all in one shot.</p>
          </div>
          <div className="flex items-end gap-2 flex-wrap">
            <div>
              <Label className="type-card-meta-label uppercase tracking-wider text-ink-secondary">Genre lens</Label>
              <Select value={genreFocus} onValueChange={setGenreFocus}>
                <SelectTrigger className="mt-1 w-44 bg-bg border-line" data-testid="news-genre-focus"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  {genres.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <ContentLanguageSelect
              value={contentLang}
              onChange={setContentLang}
              testid="news-generate-lang"
            />
            <button onClick={generate} disabled={gen.run} data-testid="news-generate-btn" className="inline-flex items-center gap-2 rounded-md bg-brand-amber px-5 py-2.5 type-button-label text-black amber-glow disabled:opacity-50">
              {gen.run ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {Object.keys(g).length ? "Regenerate" : "Generate"}
            </button>
          </div>
        </div>
        {Object.keys(g).length === 0 && (
          <div className="type-body text-ink-muted py-6 text-center border border-dashed border-line rounded-md">
            Hit <span className="text-brand-amber font-semibold">Generate</span> and the AI will write all six formats in one go.
          </div>
        )}
        {Object.keys(g).length > 0 && (
          <div className="grid md:grid-cols-2 gap-3">
            <FormatBlock icon={Radio}    title="Radio · 30s"  text={g.radio_30}  testid="news-fmt-radio30" />
            <FormatBlock icon={Radio}    title="Radio · 60s"  text={g.radio_60}  testid="news-fmt-radio60" />
            <FormatBlock icon={Radio}    title="Radio · 2min" text={g.radio_120} testid="news-fmt-radio120" />
            <FormatBlock icon={Mic}      title="Podcast"
              text={g.podcast ? `${g.podcast.title}\n\n${g.podcast.script}\n\nShow notes:\n${g.podcast.show_notes}` : ""}
              testid="news-fmt-podcast" />
            <FormatBlock icon={FileText} title="Blog article" full
              text={g.blog ? `${g.blog.headline}\n\n${g.blog.article}\n\nSEO: ${(g.blog.seo_keywords||[]).join(", ")}\nCategories: ${(g.blog.categories||[]).join(", ")}\nTags: ${(g.blog.tags||[]).join(", ")}` : ""}
              testid="news-fmt-blog"
              action={g.blog ? (
                <div className="flex items-center gap-1">
                  {s.wp_publish?.post_id && (
                    <a
                      href={s.wp_publish.link || "#"}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded bg-emerald-500/15 border border-emerald-500/30 px-1.5 py-0.5 text-[9px] font-mono text-emerald-300 hover:underline"
                      data-testid="news-blog-wp-sent"
                    >
                      ✓ ON SITE
                    </a>
                  )}
                  <button
                    onClick={publishToWP}
                    disabled={wpBusy}
                    data-testid="news-blog-send-wp"
                    title="Push just this blog article to your connected WordPress site (as a draft)"
                    className="inline-flex items-center gap-1 rounded-md border border-sky-500/40 bg-sky-500/10 px-2 py-0.5 text-[10px] font-bold text-sky-300 hover:bg-sky-500/20 transition disabled:opacity-50"
                  >
                    {wpBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Globe className="h-3 w-3" />}
                    {s.wp_publish?.post_id ? "Send again" : "Send to website"}
                  </button>
                </div>
              ) : null} />
            <SocialPostsBlock story={s} socials={g.socials} onSent={(refreshed) => { setS(refreshed); onChange(refreshed); }} />
          </div>
        )}
      </section>

      {/* Audio bulletin */}
      {Object.keys(g).length > 0 && (
        <section className="rounded-xl border border-line bg-bg-surface p-6" data-testid="news-audio-panel">
          <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
            <div>
              <div className="font-mono type-status-badge uppercase tracking-[0.2em] text-brand-amber/80">// STEP 2</div>
              <h3 className="type-section-heading text-ink-primary">AI News Anchor</h3>
              <p className="type-body text-ink-muted">Turn any of the scripts into an MP3 bulletin you can drop on-air.</p>
            </div>
            <div className="flex items-end gap-2">
              <div>
                <Label className="text-xs uppercase tracking-wider text-ink-secondary">Read</Label>
                <Select value={audioDur} onValueChange={setAudioDur}>
                  <SelectTrigger className="mt-1 w-36 bg-bg border-line" data-testid="news-audio-dur"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="radio_30">30 seconds</SelectItem>
                    <SelectItem value="radio_60">60 seconds</SelectItem>
                    <SelectItem value="radio_120">2 minutes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-ink-secondary">Voice</Label>
                <Select value={voice} onValueChange={setVoice}>
                  <SelectTrigger className="mt-1 w-44 bg-bg border-line" data-testid="news-audio-voice"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {voices.map((v) => <SelectItem key={v.id} value={v.id}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <button onClick={generateAudio} disabled={gen.audio} data-testid="news-audio-btn" className="inline-flex items-center gap-2 rounded-md bg-brand-amber px-4 py-2.5 type-button-label text-black amber-glow disabled:opacity-50">
                {gen.audio ? <Loader2 className="h-4 w-4 animate-spin" /> : <Volume2 className="h-4 w-4" />}
                {audioSrc ? "Regenerate audio" : "Generate audio"}
              </button>
            </div>
          </div>
          {audioSrc && (
            <div className="rounded-md border border-line bg-bg-elevated p-3" data-testid="news-audio-block">
              <audio controls src={audioSrc} className="w-full" data-testid="news-audio" preload="metadata" />
            </div>
          )}
        </section>
      )}

      {/* Video studio */}
      {Object.keys(g).length > 0 && (
        <NewsVideoStudio
          storyId={s.story_id}
          voice={voice}
          audioDur={audioDur}
          hasScript={!!g[audioDur]}
        />
      )}

      {/* Approval actions */}
      <section className="rounded-xl border border-line bg-bg-surface p-6 flex flex-wrap items-center justify-between gap-3" data-testid="news-approval-panel">
        <div>
          <div className="font-mono type-status-badge uppercase tracking-[0.2em] text-brand-amber/80">// STEP 3</div>
          <h3 className="type-section-heading text-ink-primary">Approve &amp; publish</h3>
          <p className="type-body text-ink-muted">When the package looks right, approve it or push straight to published.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => patch({ status: "archived" })} data-testid="news-archive" className="inline-flex items-center gap-1.5 rounded-md border border-line bg-bg-elevated px-3 py-2 type-button-label text-ink-secondary hover:text-rose-300">
            <X className="h-3.5 w-3.5" /> Archive
          </button>
          <button onClick={() => patch({ status: "approved" })} data-testid="news-approve" className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 type-button-label text-emerald-300 hover:bg-emerald-500/20 transition">
            <Check className="h-3.5 w-3.5" /> Approve
          </button>
          <button onClick={() => patch({ status: "published" })} data-testid="news-publish" className="inline-flex items-center gap-2 rounded-md bg-brand-amber px-4 py-2 type-button-label text-black amber-glow">
            <Sparkles className="h-3.5 w-3.5" /> Publish
          </button>
        </div>
      </section>
    </div>
  );
}

function SocialPostsBlock({ story, socials, onSent }) {
  const [busy, setBusy] = useState("");
  const [copied, setCopied] = useState("");
  const entries = Object.entries(socials || {});
  const SENDABLE = { facebook: "Facebook", instagram: "Instagram" };

  const send = async (plat) => {
    setBusy(plat);
    try {
      const { data } = await api.post(`/news/stories/${story.story_id}/publish-social`, { platform: plat });
      toast.success(`Posted to ${SENDABLE[plat]}!`, {
        duration: 9000,
        action: data.external_url
          ? { label: "View post", onClick: () => window.open(data.external_url, "_blank") }
          : undefined,
      });
      const refreshed = await api.get(`/news/stories/${story.story_id}`);
      onSent(refreshed.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed", { duration: 8000 });
    } finally { setBusy(""); }
  };

  const copy = (k, v) => {
    navigator.clipboard.writeText(v);
    setCopied(k);
    setTimeout(() => setCopied(""), 1500);
  };

  return (
    <div className="rounded-lg border border-line bg-bg-elevated p-4 md:col-span-2" data-testid="news-fmt-socials">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-3.5 w-3.5 text-brand-amber" />
          <span className="type-list-title text-ink-primary">Social posts</span>
        </div>
        <span className="text-[10px] text-ink-muted">Send channels one at a time — or use "Approve &amp; broadcast" for all at once</span>
      </div>
      {entries.length === 0 ? (
        <p className="type-body text-ink-muted italic">Not generated yet.</p>
      ) : (
        <div className="space-y-2">
          {entries.map(([k, v]) => {
            const sent = story.social_sends?.[k];
            return (
              <div key={k} className="rounded-md border border-line bg-bg p-3" data-testid={`news-social-${k}`}>
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <div className="flex items-center gap-2">
                    <PlatformIcon platform={k} className="h-3.5 w-3.5 shrink-0" style={{ color: getPlatformMeta(k)?.color }} />
                    <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-brand-amber/80">{k}</span>
                    {sent && (
                      <a
                        href={sent.external_url || "#"}
                        target="_blank"
                        rel="noreferrer"
                        className="rounded bg-emerald-500/15 border border-emerald-500/30 px-1.5 py-0.5 text-[9px] font-mono text-emerald-300 hover:underline"
                        data-testid={`news-social-sent-${k}`}
                      >
                        ✓ SENT
                      </a>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => copy(k, v)}
                      data-testid={`news-social-copy-${k}`}
                      className="inline-flex items-center gap-1 rounded-md border border-line bg-bg-elevated px-2 py-0.5 text-[10px] text-ink-secondary hover:text-cyan-300 transition"
                    >
                      {copied === k ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                      {copied === k ? "Copied" : "Copy"}
                    </button>
                    {SENDABLE[k] ? (
                      <button
                        onClick={() => send(k)}
                        disabled={!!busy}
                        data-testid={`news-social-send-${k}`}
                        title={`Post this to ${SENDABLE[k]} right now`}
                        className="inline-flex items-center gap-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-bold text-emerald-300 hover:bg-emerald-500/20 transition disabled:opacity-50"
                      >
                        {busy === k ? <Loader2 className="h-3 w-3 animate-spin" /> : <Rocket className="h-3 w-3" />}
                        {sent ? "Send again" : `Send to ${SENDABLE[k]}`}
                      </button>
                    ) : (
                      <span className="rounded-md border border-line bg-bg-elevated px-2 py-0.5 text-[9px] font-mono text-ink-muted" title="Direct sending for this platform isn't wired yet — copy the text instead">
                        COPY ONLY
                      </span>
                    )}
                  </div>
                </div>
                <p className="type-body text-ink-secondary whitespace-pre-wrap text-xs">{v}</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function FormatBlock({ icon: Icon, title, text, testid, full, action }) {
  const [open, setOpen] = useState(true);
  const [copied, setCopied] = useState(false);
  const has = !!text && text.trim();
  return (
    <div className={`rounded-lg border border-line bg-bg-elevated p-4 ${full ? "md:col-span-2" : ""}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon className="h-3.5 w-3.5 text-brand-amber" />
          <span className="type-list-title text-ink-primary">{title}</span>
        </div>
        <div className="flex items-center gap-1">
          {action}
          {has && (
            <button
              onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
              data-testid={`${testid}-copy`}
              className="inline-flex items-center gap-1 rounded-md border border-line bg-bg px-2 py-0.5 text-[10px] text-ink-secondary hover:text-cyan-300 transition"
            >
              {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              {copied ? "Copied" : "Copy"}
            </button>
          )}
          <button onClick={() => setOpen(!open)} className="text-ink-muted hover:text-ink-primary">
            <ChevronDown className={`h-3.5 w-3.5 transition ${open ? "" : "-rotate-90"}`} />
          </button>
        </div>
      </div>
      {open && (
        has ? (
          <p className="type-body text-ink-secondary whitespace-pre-wrap">{text}</p>
        ) : (
          <p className="type-body text-ink-muted italic">Not generated yet.</p>
        )
      )}
    </div>
  );
}


function StageTile({ label, ok, skipped, error, extra }) {
  const state = error ? "error" : ok ? "ok" : skipped ? "skip" : "miss";
  const cls = {
    ok:    "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
    skip:  "border-line bg-bg-elevated text-ink-muted",
    miss:  "border-line bg-bg-elevated text-ink-muted",
    error: "border-rose-500/40 bg-rose-500/10 text-rose-300",
  }[state];
  const dot = {
    ok: "●", skip: "○", miss: "○", error: "✕",
  }[state];
  return (
    <div className={`rounded-md border px-3 py-2 ${cls}`}>
      <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-wider">
        <span>{label}</span>
        <span>{dot}</span>
      </div>
      {extra && <div className="text-[10px] mt-1 truncate" title={extra}>{extra}</div>}
      {error && <div className="text-[10px] mt-1 truncate" title={error}>{error.slice(0, 60)}</div>}
    </div>
  );
}


function BatchProgressPanel({ job, onClose }) {
  const running = job.status !== "done";
  const pct = job.total > 0 ? Math.round((job.completed / job.total) * 100) : 0;
  return (
    <div
      data-testid="batch-progress-panel"
      className="fixed bottom-6 right-6 z-30 w-[min(420px,calc(100vw-3rem))] rounded-xl border shadow-2xl animate-fade-up"
      style={{
        background: "rgba(15, 23, 42, 0.97)",
        borderColor: "rgba(139, 92, 246, 0.45)",
        backdropFilter: "blur(12px)",
      }}
    >
      <div className="flex items-center justify-between p-4 border-b border-line">
        <div>
          <div className="font-mono type-status-badge uppercase tracking-[0.22em] text-brand-amber/80 flex items-center gap-1.5">
            <Rocket className="h-3 w-3" /> // BATCH BROADCAST
          </div>
          <div className="type-list-title text-ink-primary mt-0.5">
            {running ? `Running ${job.completed} / ${job.total}…` : `Done · ${job.succeeded} ok · ${job.failed} failed`}
          </div>
        </div>
        <button onClick={onClose} className="text-ink-muted hover:text-ink-primary" data-testid="batch-progress-close">✕</button>
      </div>

      {/* Progress bar */}
      <div className="px-4 pt-3">
        <div className="h-1.5 rounded-full overflow-hidden bg-bg-elevated">
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{
              width: `${pct}%`,
              background: "linear-gradient(90deg, #8B5CF6, #0093FF)",
            }}
          />
        </div>
        <div className="flex justify-between mt-1 text-[10px] font-mono text-ink-muted">
          <span>{job.completed} of {job.total}</span>
          <span>{pct}%</span>
        </div>
      </div>

      {/* Results list */}
      <div className="p-4 max-h-72 overflow-y-auto space-y-1.5 text-xs" data-testid="batch-results">
        {(job.results || []).map((r) => (
          <div key={r.story_id} className="flex items-center justify-between gap-2 rounded-md border border-line bg-bg-elevated/60 px-2 py-1.5">
            <div className="truncate flex-1">
              <div className="text-ink-primary truncate text-[11px]">{r.headline || r.story_id}</div>
              {r.error && <div className="text-[10px] text-rose-300 truncate">{r.error}</div>}
              {r.ok && r.stages && (
                <div className="text-[10px] font-mono text-ink-muted">
                  mix:{r.stages.mix ? "✓" : "—"} · video:{r.stages.video ? "✓" : "—"} · text:{r.stages.text_posts} · vid:{r.stages.video_posts}
                </div>
              )}
            </div>
            <span className={`shrink-0 h-2 w-2 rounded-full ${r.ok ? "bg-emerald-400" : "bg-rose-400"}`} />
          </div>
        ))}
        {running && job.results.length < job.total && (
          <div className="flex items-center gap-2 text-[11px] text-ink-muted py-2 px-2">
            <Loader2 className="h-3 w-3 animate-spin text-brand-amber" />
            Processing story {job.completed + 1} of {job.total}…
          </div>
        )}
      </div>
    </div>
  );
}
